import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1rem;
  text-align: left;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
`;

const SectionSubtitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0.5rem 0 1.2rem;
  text-align: left;
  letter-spacing: 1px;
`;

const GroupHeader = styled.h4`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0.25rem 0 0.5rem;
  letter-spacing: 1px;
  animation: ${fadeUp} 260ms ease both;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 820px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 14px 0;
  font-size: 1.35rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.5px;

  &::before {
    content: attr(data-idx) '.';
    color: #64B6FF;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    margin-top: 0;
    display: inline-block;
    width: 24px;
    flex: 0 0 24px;
    text-align: right;
  }
`;

const Subline = styled.div`
  font-size: 1.05rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin: 6px 0 0 0;
`;

const LinkAnchor = styled.a`
  color: #64B6FF;
  text-decoration: none;
  border-bottom: 1px dashed rgba(100, 182, 255, 0.35);
  padding-bottom: 1px;
  transition: color 160ms ease, border-color 160ms ease, text-shadow 160ms ease;

  &:hover {
    color: #9cd1ff;
    border-color: rgba(156, 209, 255, 0.6);
    text-shadow: 0 0 8px rgba(100, 182, 255, 0.45);
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingFormatosComplexos() {
  const location = useLocation();
  const navigate = useNavigate();

  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  const [groupIndex, setGroupIndex] = useState(0); // 0..2
  const [itemIndex, setItemIndex] = useState(0); // índice do subformato dentro do grupo
  const [visibleCount, setVisibleCount] = useState(1); // para revelar progressivamente no grupo 0

  const groups = [
    {
      title: 'Elementos da Oferta',
      items: [
        { label: 'Globo', ref: 'https://www.instagram.com/reel/DMVsM3NMSuc/?igsh=bjM2M3l2bzBvcDV5', ex: 'https://drive.google.com/file/d/1D0pOF3OXzgHKYydBBEQciuUD0QTepf_T/view?usp=drive_link' },
        { label: 'Proximidades', ref: 'https://www.instagram.com/reel/DHBWjRGRA0j/?igsh=cXVzdDZobGp5MGkw' },
        { label: 'Localização 3d atrás', ref: 'https://www.instagram.com/reel/DIRsFALSO9n/?igsh=MXBzZmh4YWs3Mmg4bA==' },
        { label: 'Checklist', ref: 'https://www.instagram.com/reel/DJSeYXRBYBA/?igsh=MW5kM3ZobjNzcTkyZA==', ex: 'https://drive.google.com/file/d/1fBH9djLflblf0fQnCtiwc3XONh9qCnNN/view?usp=sharing' },
      ],
    },
    {
      title: 'Curiosidade',
      items: [
        { label: 'Vinho', ref: 'https://www.instagram.com/reel/DKxRmKzNC50/?igsh=MWtvaDBvOTNmdzVxdA==' },
      ],
    },
    {
      title: 'Lifestyle',
      items: [
        { label: 'E se fosse possível ter o melhor dos dois mundos', ref: 'https://www.instagram.com/reel/DHwuiAbyFgw/?igsh=OGoxZjRuMHRiOHJq' },
      ],
    },
  ];

  const goBackRoute = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos/basicos${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    // Grupo 0: revelar itens progressivamente na mesma página
    if (groupIndex === 0) {
      const maxItems = groups[0].items.length;
      setVisibleCount((v) => {
        if (v < maxItems) return v + 1;
        // todos visíveis -> ir para próximo grupo
        setGroupIndex(1);
        setItemIndex(0);
        return v;
      });
      return;
    }

    // Demais grupos: um por vez (na prática têm 1 item)
    const lastItemIdx = groups[groupIndex].items.length - 1;
    if (itemIndex < lastItemIdx) {
      setItemIndex(itemIndex + 1);
      return;
    }
    if (groupIndex < groups.length - 1) {
      setGroupIndex(groupIndex + 1);
      setItemIndex(0);
      return;
    }
    // Último item do último grupo -> ir para o Script da Bianca
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/script-bianca${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  const goPrev = () => {
    if (groupIndex === 0) {
      setVisibleCount((v) => {
        if (v > 1) return v - 1;
        goBackRoute();
        return v;
      });
      return;
    }

    setItemIndex((i) => {
      if (i > 0) return i - 1;
      if (groupIndex > 0) {
        // voltar grupo; se voltar para o grupo 0, mostrar todos os itens já vistos? por padrão, mostrar todos (4)
        setGroupIndex((g) => g - 1);
        if (groupIndex - 1 === 0) setVisibleCount(groups[0].items.length);
        return 0;
      }
      goBackRoute();
      return i;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search, groups.length, groupIndex]);

  const current = groups[groupIndex];
  const currentItem = current.items[itemIndex];

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim}>
        <BackButton onClick={(e) => { e.stopPropagation(); goBackRoute(); }} aria-label="Voltar">‹</BackButton>
        <NextButton onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Avançar">›</NextButton>
        <Content>
          <SectionTitle>Formatos de Headlines</SectionTitle>
          <SectionSubtitle>Produções complexas:</SectionSubtitle>

          <GroupHeader>{current.title}</GroupHeader>
          <List>
            {groupIndex === 0 ? (
              current.items.slice(0, visibleCount).map((it, i) => (
                <Item key={i} data-idx={i + 1}>
                  <div>
                    {it.label}
                    {it.ref && (
                      <Subline>
                        Ref: <LinkAnchor href={it.ref} title={it.ref} target="_blank" rel="noopener noreferrer">Ver no Instagram</LinkAnchor>
                      </Subline>
                    )}
                    {it.ex && (
                      <Subline>
                        ex: <LinkAnchor href={it.ex} title={it.ex} target="_blank" rel="noopener noreferrer">Ver exemplo</LinkAnchor>
                      </Subline>
                    )}
                  </div>
                </Item>
              ))
            ) : (
              <Item data-idx={itemIndex + 1}>
                <div>
                  {currentItem.label}
                  {currentItem.ref && (
                    <Subline>
                      Ref: <LinkAnchor href={currentItem.ref} title={currentItem.ref} target="_blank" rel="noopener noreferrer">Ver no Instagram</LinkAnchor>
                    </Subline>
                  )}
                  {currentItem.ex && (
                    <Subline>
                      ex: <LinkAnchor href={currentItem.ex} title={currentItem.ex} target="_blank" rel="noopener noreferrer">Ver exemplo</LinkAnchor>
                    </Subline>
                  )}
                </div>
              </Item>
            )}
          </List>
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingFormatosComplexos;
